import { type FC, useCallback, useEffect, useMemo, useState } from "react";

import type { DashboardOverview } from "../../types";

import { getAdminOverviewAsAdmin } from "../../dashboard-client";
import { formatRelativeTime } from "../../utils/time";
import { Alert } from "../common/Alert";
import { DataTable } from "../common/DataTable";
import { HudPanel } from "../common/HudPanel";
import { HudStatusPill } from "../common/HudStatusPill";
import { LoadingState } from "../common/LoadingState";
import { MetricCard } from "../common/MetricCard";
import { PanelSection } from "../common/PanelSection";
import { NodeTelemetryTable } from "../dashboard/NodeTelemetryTable";

export const AdminSystemTab: FC = () => {
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await getAdminOverviewAsAdmin({});
      setOverview(data);
    } catch (_error) {
      setError(
        _error instanceof Error
          ? _error.message
          : "Failed to load system overview",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchOverview();
  }, [fetchOverview]);

  if (loading) {
    return <LoadingState message="Loading system health…" />;
  }
  if (error) {
    return <Alert variant="error">{error}</Alert>;
  }
  if (!overview) {
    return null;
  }

  const onlineNodes = overview.nodes.filter(
    (n) => n.status === "online",
  ).length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <MetricCard
          label="Total Nodes"
          value={overview.nodes.length}
          variant="indigo"
          size="lg"
        />
        <MetricCard
          label="Online Nodes"
          value={onlineNodes}
          variant="emerald"
          size="lg"
        />
        <MetricCard
          label="Queued Tasks"
          value={overview.queue.pendingCount}
          variant="amber"
          size="lg"
        />
        <MetricCard
          label="Total Tasks"
          value={overview.tasks.summary.total}
          variant="violet"
          size="lg"
        />
      </div>

      <RuntimeEncryptionPanel overview={overview} />

      <NodeTelemetryTable nodes={overview.nodes} />

      {overview.queue.pending.length > 0 && (
        <PanelSection
          title="Global Queue"
          badge={`${overview.queue.pending.length} pending entries`}
        >
          <DataTable responsive>
            <DataTable.Header>
              <tr>
                <DataTable.HeaderCell>Task ID</DataTable.HeaderCell>
                <DataTable.HeaderCell>Ticket</DataTable.HeaderCell>
                <DataTable.HeaderCell>Depends On</DataTable.HeaderCell>
                <DataTable.HeaderCell>Queued</DataTable.HeaderCell>
              </tr>
            </DataTable.Header>
            <DataTable.Body>
              {overview.queue.pending.map((entry) => (
                <DataTable.Row key={entry.taskId}>
                  <DataTable.Cell className="font-mono text-xs text-gray-900 dark:text-white">
                    {entry.taskId}
                  </DataTable.Cell>
                  <DataTable.Cell className="text-xs text-gray-700 dark:text-gray-300">
                    {entry.ticketId ?? "—"}
                  </DataTable.Cell>
                  <DataTable.Cell className="text-xs text-gray-700 dark:text-gray-300">
                    {entry.dependsOnTaskId ?? "—"}
                  </DataTable.Cell>
                  <DataTable.Cell className="whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                    {formatRelativeTime(entry.enqueuedAt)}
                  </DataTable.Cell>
                </DataTable.Row>
              ))}
            </DataTable.Body>
          </DataTable>
        </PanelSection>
      )}
    </div>
  );
};

interface RuntimeEncryptionPanelProps {
  overview: DashboardOverview;
}

const RuntimeEncryptionPanel: FC<RuntimeEncryptionPanelProps> = ({
  overview,
}) => {
  const dashboardFp = overview.dashboard?.encryptionFingerprint ?? null;
  const orchestratorFp = overview.orchestrator?.encryptionFingerprint ?? null;
  const { nodes } = overview;

  const { mismatched, distinctValues } = useMemo(() => {
    const values = new Set<string>();
    if (dashboardFp) {
      values.add(dashboardFp);
    }
    if (orchestratorFp) {
      values.add(orchestratorFp);
    }
    for (const node of nodes) {
      const fp = node.encryptionFingerprint;
      if (fp) {
        values.add(fp);
      }
    }
    return { distinctValues: values, mismatched: values.size > 1 };
  }, [dashboardFp, orchestratorFp, nodes]);

  if (
    !dashboardFp &&
    !orchestratorFp &&
    nodes.every((n) => !n.encryptionFingerprint)
  ) {
    return null;
  }

  return (
    <HudPanel
      title="Runtime Encryption"
      subtitle="Per-runtime secret-store key fingerprints"
      tone={mismatched ? "danger" : "success"}
      actions={
        <HudStatusPill
          label={
            mismatched
              ? `MISMATCH · ${distinctValues.size} distinct contexts`
              : "context aligned"
          }
          tone={mismatched ? "danger" : "success"}
          pulse={mismatched}
        />
      }
    >
      <div className="grid gap-2 sm:grid-cols-2">
        <FingerprintRow label="Dashboard" fingerprint={dashboardFp} />
        <FingerprintRow
          label="Orchestrator"
          fingerprint={orchestratorFp}
          missingLabel={
            overview.orchestrator === null ? "unreachable" : undefined
          }
        />
        {nodes.map((node) => (
          <FingerprintRow
            key={node.id}
            label={`Node · ${node.id}`}
            fingerprint={node.encryptionFingerprint ?? null}
            missingLabel={node.status === "offline" ? "offline" : undefined}
          />
        ))}
      </div>
      {mismatched && (
        <p className="mt-3 font-mono text-[11px] text-rose-700 dark:text-rose-300">
          Encryption contexts differ across runtimes. Secrets sealed in one
          context cannot be revealed in another — reseal the affected
          credentials or align the secret-store keys.
        </p>
      )}
    </HudPanel>
  );
};

const FingerprintRow: FC<{
  label: string;
  fingerprint: string | null;
  missingLabel?: string;
}> = ({ label, fingerprint, missingLabel }) => (
  <div className="flex items-center justify-between gap-3 border border-slate-200 bg-slate-50/70 px-3 py-2 dark:border-slate-700 dark:bg-slate-900/40">
    <span className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
      {label}
    </span>
    {fingerprint ? (
      <span className="font-mono text-[11px] font-bold text-emerald-700 dark:text-emerald-300">
        [ENC {fingerprint}]
      </span>
    ) : (
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
        {missingLabel ?? "—"}
      </span>
    )}
  </div>
);
